(() => {
  const localizedStrings = window.themeCustomStrings?.lydPlus || {};
  const lydPlusSettings = window.themeCustomSettings?.lydPlus || {};
  const LYD_PLUS_HANDLE = lydPlusSettings.handle || "lyd-plus";
  const LYD_PLUS_TAG = "brugt lyd+";
  const LYD_PLUS_BADGE_LABEL = "LYD+";
  const LYD_PLUS_BADGE_TEXT = localizedStrings.badgeText || "Save 10%";
  const LYD_PLUS_REGULAR_PRICE_TEXT = localizedStrings.regularPrice || "Regular price";
  const LYD_PLUS_DISCOUNT_MULTIPLIER = 0.9;
  const LYD_PLUS_PASSWORD_HASH = lydPlusSettings.passwordHash || "";
  const LYD_PLUS_PASS_PARAM = `${LYD_PLUS_HANDLE}-pass`;
  const LYD_PLUS_HASH_STORAGE_KEY = `${LYD_PLUS_HANDLE.replace(/-/g, "_")}_hash`;
  const LYD_PLUS_UNLOCKED_CLASS = "lyd-plus-unlocked";
  const LYD_PLUS_GATE_SELECTOR = ".lyd-plus-gate";
  const LYD_PLUS_GATE_STYLES_SELECTOR = ".lyd-plus-gate__styles";
  const LYD_PLUS_GATE_FORM_SELECTOR = ".lyd-plus-gate__form";
  const LYD_PLUS_GATE_ERROR_SELECTOR = ".lyd-plus-gate__error";
  const productCache = new Map();
  const checkedCards = new WeakSet();
  const checkedProductPriceLists = new WeakSet();
  const enabledPriceLists = new WeakSet();
  let overlayRenderTimer;

  const sha256 = async (str) => {
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
    return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
  };

  const getUrlPass = (hash = window.location.hash) => {
    const params = new URLSearchParams(String(hash || "").replace(/^#/, ""));
    const value = params.get(LYD_PLUS_PASS_PARAM);

    return value?.split("?")[0] || null;
  };

  const removeUrlPassParams = () => {
    if (window.location.hash.includes(LYD_PLUS_PASS_PARAM)) {
      history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    }
  };

  const pageCanAcceptUrlPass = () => (
    lydPlusSettings.acceptsUrlPass || Boolean(document.querySelector(LYD_PLUS_GATE_SELECTOR))
  );

  const hideLydPlusGates = () => {
    document.querySelectorAll(LYD_PLUS_GATE_STYLES_SELECTOR).forEach((style) => style.remove());
    document.querySelectorAll(LYD_PLUS_GATE_SELECTOR).forEach((gate) => gate.setAttribute("hidden", ""));
  };

  const passwordMatches = async (password) => (
    Boolean(LYD_PLUS_PASSWORD_HASH) && (await sha256(password)) === LYD_PLUS_PASSWORD_HASH
  );

  const setLydPlusUnlocked = () => {
    document.documentElement.classList.add(LYD_PLUS_UNLOCKED_CLASS);
    hideLydPlusGates();
    removeUrlPassParams();
  };

  const unlockLydPlus = ({ persist = true, broadcast = true } = {}) => {
    setLydPlusUnlocked();

    if (persist && LYD_PLUS_PASSWORD_HASH) {
      try {
        localStorage.setItem(LYD_PLUS_HASH_STORAGE_KEY, LYD_PLUS_PASSWORD_HASH);
      } catch (e) {}
    }

    if (broadcast && LYD_PLUS_PASSWORD_HASH) {
      window.dispatchEvent(new CustomEvent("lyd-plus:unlock", { detail: { hash: LYD_PLUS_PASSWORD_HASH } }));
    }
  };

  const syncUnlockState = () => {
    if (lydPlusSettings.customerHasAccess) {
      unlockLydPlus({ persist: false, broadcast: false });
      return;
    }

    try {
      const storedHash = localStorage.getItem(LYD_PLUS_HASH_STORAGE_KEY);

      if (!storedHash) {
        return;
      }

      if (LYD_PLUS_PASSWORD_HASH && storedHash === LYD_PLUS_PASSWORD_HASH) {
        unlockLydPlus({ persist: false, broadcast: false });
      } else {
        localStorage.removeItem(LYD_PLUS_HASH_STORAGE_KEY);
      }
    } catch (e) {}
  };

  const saveUrlPass = async (hash = window.location.hash) => {
    const urlPass = getUrlPass(hash);

    if (!urlPass) {
      return;
    }

    if (pageCanAcceptUrlPass() && await passwordMatches(urlPass)) {
      unlockLydPlus();
    }

    removeUrlPassParams();
  };

  const processUrlPass = (hash) => {
    saveUrlPass(hash).catch(() => removeUrlPassParams());
  };

  document.addEventListener("submit", async (event) => {
    const form = event.target.closest?.(LYD_PLUS_GATE_FORM_SELECTOR);

    if (!form) {
      return;
    }

    event.preventDefault();

    const password = form.elements.lyd_plus_password?.value;

    if (password && await passwordMatches(password)) {
      unlockLydPlus();
      return;
    }

    form.closest(LYD_PLUS_GATE_SELECTOR)?.querySelector(LYD_PLUS_GATE_ERROR_SELECTOR)?.removeAttribute("hidden");
  });

  syncUnlockState();
  processUrlPass();
  window.addEventListener("hashchange", () => processUrlPass());
  window.addEventListener("pageshow", () => processUrlPass());
  document.documentElement.addEventListener("hashchange:simulate", (event) => processUrlPass(event.detail?.hash));

  const isUnlocked = () => document.documentElement.classList.contains(LYD_PLUS_UNLOCKED_CLASS);

  const tagsContainLydPlus = (tags) => {
    if (typeof tags === "string") {
      tags = tags.split(",");
    }

    return Array.isArray(tags) && tags.some((tag) => tag.trim().toLowerCase() === LYD_PLUS_TAG);
  };

  const fetchJson = async (path) => {
    const routesRoot = window.Shopify?.routes?.root || "/";

    return fetch(`${routesRoot}${path}`, {
      headers: { Accept: "application/json" },
      cache: "no-store"
    }).then((response) => response.ok ? response.json() : null).catch(() => null);
  };

  const fetchProduct = async (handle) => {
    if (!handle) {
      return null;
    }

    if (!productCache.has(handle)) {
      productCache.set(handle, fetchJson(`products/${encodeURIComponent(handle)}.js`));
    }

    return productCache.get(handle);
  };

  const getLydPlusPrice = (price) => Math.round(price * LYD_PLUS_DISCOUNT_MULTIPLIER);

  const getPriceTextNode = (priceElement) => {
    if (!priceElement) {
      return null;
    }

    if (priceElement.lastChild?.nodeType === Node.TEXT_NODE) {
      return priceElement.lastChild;
    }

    const textNode = document.createTextNode("");
    priceElement.append(textNode);
    return textNode;
  };

  const setPriceText = (priceElement, price) => {
    const textNode = getPriceTextNode(priceElement);

    if (textNode && Number.isFinite(price)) {
      textNode.textContent = ` ${Shopify.formatMoney(price)}`;
    }
  };

  const ensureCompareAtPrice = (priceList, salePrice) => {
    let compareAtPrice = priceList.querySelector("compare-at-price");

    if (compareAtPrice) {
      return compareAtPrice;
    }

    compareAtPrice = document.createElement("compare-at-price");

    const sizeClass = priceList.classList.contains("price-list--product")
      ? "h5"
      : ["h6", "h5", "h4"].find((className) => salePrice.classList.contains(className));

    compareAtPrice.className = [sizeClass, "text-subdued", "line-through"].filter(Boolean).join(" ");

    if (salePrice.hasAttribute("form")) {
      compareAtPrice.setAttribute("form", salePrice.getAttribute("form"));
    }

    const hiddenLabel = document.createElement("span");
    hiddenLabel.className = "sr-only";
    hiddenLabel.textContent = LYD_PLUS_REGULAR_PRICE_TEXT;
    compareAtPrice.append(hiddenLabel, document.createTextNode(" "));
    priceList.append(compareAtPrice);

    return compareAtPrice;
  };

  const applyLydPlusPriceList = (priceList, variant) => {
    if (!priceList || !variant?.price || !window.Shopify?.formatMoney) {
      return;
    }

    const salePrice = priceList.querySelector("sale-price");

    if (!salePrice) {
      return;
    }

    const basePrice = Number(variant.price);
    const lydPlusPrice = getLydPlusPrice(basePrice);
    const compareAtPrice = ensureCompareAtPrice(priceList, salePrice);

    enabledPriceLists.add(priceList);

    setPriceText(salePrice, lydPlusPrice);
    setPriceText(compareAtPrice, basePrice);

    salePrice.classList.remove("text-subdued");
    salePrice.classList.add("text-on-sale");
    compareAtPrice.hidden = false;
  };

  const ensureBadgeList = (container, options = {}) => {
    if (!container) {
      return null;
    }

    let badgeList = container.querySelector(":scope > badge-list.badge-list");

    if (!badgeList) {
      badgeList = document.createElement("badge-list");
      badgeList.className = `badge-list${options.vertical ? " badge-list--vertical" : ""}`;
      container.prepend(badgeList);
    }

    badgeList.hidden = false;
    return badgeList;
  };

  const upsertLydPlusBadge = (container, options = {}) => {
    const badgeList = ensureBadgeList(container, options);

    if (!badgeList) {
      return;
    }

    let badge = badgeList.querySelector(".lyd-plus-badge");

    if (!badge) {
      badge = document.createElement("span");
      badge.className = "badge badge--custom lyd-plus-badge";
    }

    badge.removeAttribute("hidden");
    badge.textContent = LYD_PLUS_BADGE_LABEL;

    const usedBadge = [...badgeList.querySelectorAll(".badge--custom")]
      .find((candidate) => (
        candidate !== badge
        && candidate.textContent.trim().toLowerCase() === "brugt"
      ));

    if (usedBadge) {
      badgeList.insertBefore(badge, usedBadge);
      return;
    }

    if (badge.parentElement !== badgeList || badge !== badgeList.firstElementChild) {
      badgeList.prepend(badge);
    }
  };

  const upsertLydPlusSaleBadge = (container, options = {}) => {
    const badgeList = ensureBadgeList(container, options);

    if (!badgeList) {
      return;
    }

    let badge = badgeList.querySelector("on-sale-badge.badge--on-sale");

    if (!badge) {
      badge = document.createElement("on-sale-badge");
      badge.className = "badge badge--on-sale";
      badgeList.append(badge);
    }

    badge.removeAttribute("hidden");
    badge.textContent = LYD_PLUS_BADGE_TEXT;
  };

  const upsertProductPageLydPlusBadges = () => {
    const badgeContainer = document.querySelector('.product-info__block-item[data-block-type="badges"]');

    upsertLydPlusBadge(badgeContainer);
    upsertLydPlusSaleBadge(badgeContainer);
  };

  const getInitialVariant = (product, form) => {
    const selectedVariantId = form?.querySelector('[name="id"]')?.value;

    if (selectedVariantId) {
      const selectedVariant = product.variants?.find((variant) => String(variant.id) === selectedVariantId);

      if (selectedVariant) {
        return selectedVariant;
      }
    }

    return product.variants?.find((variant) => variant.available) || product.variants?.[0];
  };

  const getProductPageHandle = () => {
    const variantPicker = document.querySelector("variant-picker[handle]");

    if (variantPicker) {
      return variantPicker.getAttribute("handle");
    }

    const productPathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return productPathMatch ? decodeURIComponent(productPathMatch[1]) : null;
  };

  const renderCardOverlay = async (card) => {
    if (checkedCards.has(card)) {
      return;
    }

    checkedCards.add(card);

    const product = await fetchProduct(card.getAttribute("handle"));

    if (!product || !tagsContainLydPlus(product.tags)) {
      return;
    }

    const variant = getInitialVariant(product);
    const badgeContainer = card.querySelector(".product-card__figure");

    upsertLydPlusBadge(badgeContainer, { vertical: true });
    upsertLydPlusSaleBadge(badgeContainer, { vertical: true });
    applyLydPlusPriceList(card.querySelector("price-list"), variant || { price: product.price });
  };

  const getProductPriceList = () => document.querySelector(
    '.product-info__block-item[data-block-type="price"] price-list.price-list--product'
  );

  const renderProductPriceList = async () => {
    const priceList = getProductPriceList();
    const variantPicker = document.querySelector("variant-picker[handle]");

    if (!priceList || checkedProductPriceLists.has(priceList)) {
      return;
    }

    checkedProductPriceLists.add(priceList);

    const product = await fetchProduct(getProductPageHandle());

    if (!product || !tagsContainLydPlus(product.tags)) {
      return;
    }

    const formId = (
      variantPicker?.getAttribute("form")
      || document.querySelector(".product-gallery[form]")?.getAttribute("form")
    );
    const form = document.forms[formId];
    const variant = getInitialVariant(product, form);

    upsertProductPageLydPlusBadges();
    applyLydPlusPriceList(priceList, variant || { price: product.price });
  };

  const renderLydPlusOverlays = () => {
    if (!isUnlocked()) {
      return;
    }

    document.querySelectorAll("product-card[handle]").forEach(renderCardOverlay);
    renderProductPriceList();
  };

  const scheduleLydPlusOverlays = () => {
    window.clearTimeout(overlayRenderTimer);
    overlayRenderTimer = window.setTimeout(renderLydPlusOverlays, 50);
  };

  const refreshDynamicDom = () => {
    renderLydPlusOverlays();
  };

  const scheduleDynamicDomRefresh = () => {
    if (!isUnlocked()) {
      return;
    }

    scheduleLydPlusOverlays();
  };

  document.addEventListener("variant:change", (event) => {
    if (!isUnlocked() || !event.detail?.variant) {
      return;
    }

    requestAnimationFrame(() => {
      const priceList = getProductPriceList();

      if (priceList && enabledPriceLists.has(priceList)) {
        upsertProductPageLydPlusBadges();
        applyLydPlusPriceList(priceList, event.detail.variant);
      }
    });
  });

  window.addEventListener("lyd-plus:unlock", (event) => {
    if (event.detail?.hash === LYD_PLUS_PASSWORD_HASH) {
      setLydPlusUnlocked();
      scheduleDynamicDomRefresh();
    }
  });

  const bodyObserver = new MutationObserver(scheduleDynamicDomRefresh);
  document.addEventListener("DOMContentLoaded", () => {
    bodyObserver.observe(document.body, { childList: true, subtree: true });
    refreshDynamicDom();
  });
})();
